import type { components } from './generated/public'

type Schemas = components['schemas']

export type PublicStatus = Schemas['PublicStatus']
export type LiveMatchCountResponse = Schemas['LiveMatchCountResponse']
export type LiveMatchCountEventEnvelope = Schemas['LiveMatchCountEventEnvelope']
export type LiveMatch = Schemas['LiveMatch']
export type LiveStateTimer = Schemas['LiveStateTimer']
export type LiveTeam = Schemas['LiveTeam']
export type LivePlayer = Schemas['LivePlayer']
export type UnitPermanentBuff = Schemas['UnitPermanentBuff']
export type UnitPermanentBuffState = Schemas['UnitPermanentBuffState']
export type OwnedUnitInventoryState = Schemas['OwnedUnitInventoryState']
export type OwnedUnitState = Schemas['OwnedUnitState']
export type LiveMatchListResponse = Schemas['LiveMatchListResponse']
export type LiveMatchEnded = Schemas['LiveMatchEnded']
export type LiveMatchEventEnvelope = Schemas['LiveMatchEventEnvelope']
export type MatchSummary = Schemas['MatchSummary']
export type MatchDetail = Schemas['MatchDetail']
export type MatchPlayer = Schemas['MatchPlayer']
export type PlayerMatchChatMessage = Schemas['PlayerMatchChatMessage']
export type PlayerMatchChatResponse = Schemas['PlayerMatchChatResponse']
export type PlayerMatchChatUnavailableReason = NonNullable<
  PlayerMatchChatResponse['unavailable_reason']
>
export type MatchListResponse = Schemas['MatchListResponse']
export type PlayerSummary = Schemas['PlayerSummary']
export type PlayerProfile = Schemas['PlayerProfile']
export type PublicPlayerStatus = Schemas['PublicPlayerStatus']
export type PlayerMatchListResponse = Schemas['PlayerMatchListResponse']
export type PlayerPeer = Schemas['PlayerPeer']
export type PlayerPeersResponse = Schemas['PlayerPeersResponse']
export type PlayerListResponse = Schemas['PlayerListResponse']
export type PatchHeroesResponse = Schemas['PatchHeroesResponse']
export type HeroProfileResponse = Schemas['HeroProfileResponse']
export type HeroProfileMatchup = Schemas['HeroProfileMatchup']
export type HeroMatchListResponse = Schemas['HeroMatchListResponse']
export type HeroMatchSummary = Schemas['HeroMatchSummary']
export type HomeFeedResponse = Schemas['HomeFeedResponse']
export type HomeFeedRoleLeader = Schemas['HomeFeedRoleLeader']
export type HomeFeedRecord = Schemas['HomeFeedRecord']
export type HomeFeedEvent = Schemas['HomeFeedEvent']
export type PlayerFeedResponse = Schemas['PlayerFeedResponse']
export type HeroStats = Schemas['HeroStats']
export type PlayerMe = Schemas['PlayerMe'] & { moderator?: boolean }
export type PlayerAccessStatus = {
  required: boolean
  allowed: boolean
  status: 'not_required' | 'approved' | 'verification_required'
  contact_url?: string | null
  welcome_pending?: boolean
}
export type PlayerAccessApplication = Schemas['PlayerAccessApplication']
export type PlayerAccessApplicationState = Omit<Schemas['PlayerAccessApplicationState'], 'access'> & {
  access: PlayerAccessStatus
}
export type PlayerAccessApplicationSubmit = Schemas['PlayerAccessApplicationSubmit']
export type PlayerCommunicationEffect = Schemas['CommunicationSanctionEffect']
export type PlayerCommunicationRestrictionEffect = Schemas['PlayerCommunicationRestrictionEffect']
export type PlayerCommunicationRestrictions = Schemas['PlayerCommunicationRestrictions']
export type PlayerChatSendCapabilities = Schemas['PlayerChatSendCapabilities']
type GeneratedPlayerChatCapabilities = Schemas['PlayerChatCapabilities']
export type PlayerChatCapabilities = Omit<GeneratedPlayerChatCapabilities, 'send'> & {
  // Optional only for rolling deploys against a backend that predates
  // communication sanctions. An absent shape preserves the old send policy.
  send?: Partial<PlayerChatSendCapabilities>
}
type GeneratedPlayerCapabilities = Schemas['PlayerMeResponse']['capabilities']
export type PlayerMeResponse = Omit<
  Schemas['PlayerMeResponse'],
  'player' | 'access' | 'capabilities' | 'restrictions'
> & {
  player: PlayerMe
  access: PlayerAccessStatus
  capabilities: Omit<GeneratedPlayerCapabilities, 'chat'> & {
    chat: PlayerChatCapabilities
  }
  restrictions?: {
    communication?: PlayerCommunicationRestrictions
  }
}
export type LogoutResponse = Schemas['LogoutResponse']
export type PlayerMatchmakerQueue = Schemas['PlayerMatchmakerQueue'] & { private?: boolean }
export type PlayerMatchmakerState = Omit<
  Schemas['PlayerMatchmakerState'],
  'player' | 'queues' | 'party_roster_locked' | 'party_roster_lock_reason'
> & {
  player: PlayerMe
  queues: PlayerMatchmakerQueue[]
  // Optional in the browser compatibility type so a rolling frontend deploy
  // fails closed against a backend that predates this capability.
  party_roster_locked?: boolean
  party_roster_lock_reason?: 'ready_check' | 'runtime_roster_pending'
}
export type PlayerQueueBan = Schemas['PlayerQueueBan']
export type ResultResponse = Schemas['ResultResponse']
export type PlayerParty = Schemas['Party']
export type PlayerPartyMember = Schemas['PartyMember']
export type PlayerPartyInvite = Schemas['PartyInvite']
type PlayerPartyETagCompatibility<T> = Omit<T, 'party_etag'> & {
  party_etag?: string | null
}
export type PlayerPartyState =
  PlayerPartyETagCompatibility<Schemas['PartyStateResponse']>
export type PlayerPartyResponse =
  PlayerPartyETagCompatibility<Schemas['PartyResponse']>
export type PlayerPartyInviteResponse =
  PlayerPartyETagCompatibility<Schemas['PartyInviteResponse']>
export type PlayerPartyInvitesDeclineAllResponse =
  Schemas['PartyInvitesDeclineAllResponse']
export type PlayerSocialState = Schemas['PlayerSocialState']
export type PlayerSocialFriend = Schemas['SocialFriend']
export type PlayerGameActivity = Schemas['PlayerGameActivity']
export type PlayerSocialFriendRequest = Schemas['SocialFriendRequest']
export type PlayerSocialNotificationIgnore = Schemas['SocialNotificationIgnore']
export type PlayerSocialEventEnvelope = Schemas['SocialEventEnvelope']
export type PlayerPresenceHeartbeat = Schemas['PresenceHeartbeatResponse']
export type PlayerNotificationDeliveryTelemetry =
  Schemas['PlayerNotificationDeliveryTelemetryRequest']
export type PlayerPushConfig = Schemas['PlayerWebPushConfigResponse']
export type PlayerPushSubscription = Schemas['PlayerWebPushSubscriptionRequest']
export type PlayerChatKind = 'global' | 'party' | 'dm'
export type PlayerChatPeer = Schemas['SocialPlayer']
export type PlayerChatConversation = Omit<
  Schemas['PlayerChatConversation'],
  'kind' | 'party_id' | 'peer'
> & {
  kind: PlayerChatKind
  party_id?: string
  peer?: PlayerChatPeer
  last_outgoing_at?: string
  recent_hidden?: boolean
}
export type PlayerChatConversationPage = Omit<
  Schemas['PlayerChatConversationPage'],
  'conversations'
> & {
  conversations: PlayerChatConversation[]
}
export type PlayerChatMessageType = Schemas['PlayerChatMessage']['message_type']
export type PlayerChatSystemEvent = Schemas['PlayerChatSystemEvent']
export type PlayerChatSystemEventType = PlayerChatSystemEvent['type']
export type PlayerChatMessage = Omit<
  Schemas['PlayerChatMessage'],
  'message_type' | 'system_event'
> & {
  // Optional only as a rolling-deploy compatibility fallback. Messages from
  // the current backend always carry the discriminant.
  message_type?: PlayerChatMessageType
  system_event?: PlayerChatSystemEvent
}
export type PlayerChatHistory = Schemas['PlayerChatHistory']
export type PlayerChatSendResponse = Omit<
  Schemas['PlayerChatSendResponse'],
  'conversation'
> & {
  conversation: PlayerChatConversation
}
export type PlayerChatReadCursor = Schemas['PlayerChatReadCursor']
export type PlayerChatReportResponse = Schemas['PlayerChatReportResponse']
export type PlayerPartySnapshot = {
  state: PlayerPartyState
  etag: string | null
}
export type PlayerPartyMutationResult<T> = {
  data: T
  etag: string | null
}
export type MatchScoringStatus = 'scored' | 'not_scored'
export type LobbyCategory = 'ranked' | 'normal' | 'tournament' | 'custom'
export type PlayerPeerRelation = Schemas['PlayerPeerRelation']
export type PlayerSort = 'matches_played' | 'win_rate' | 'kda' | 'avg_kills' | 'avg_deaths' | 'avg_assists' | 'total_kills' | 'total_deaths' | 'total_assists' | 'total_duration_seconds' | 'last_match_at'
export type PlayerSortDir = 'desc' | 'asc'
export type PublicRequestOptions = {
  fresh?: boolean
  signal?: AbortSignal
}
export type MatchListFilters = {
  gameMode?: number
  lobbyType?: number
  lobbyTypes?: readonly number[]
  lobbyCategory?: LobbyCategory
  heroID?: number
  scoringStatus?: MatchScoringStatus
}

const apiBase = '/api/public/v1'
const playerApiBase = '/api/player/v1'
export const publicLiveEventsURL = `${apiBase}/live/events`
export const publicLiveCountEventsURL = `${apiBase}/live/count/events`
export const playerEventsURL = `${playerApiBase}/events`
export const publicVisibleLobbyTypes = [7, 2, 0] as const

export class PlayerSessionUnauthorizedError extends Error {
  constructor() {
    super('player session is not authenticated')
    this.name = 'PlayerSessionUnauthorizedError'
  }
}

export class PublicRequestError extends Error {
  readonly status: number
  readonly code: string | null
  readonly retryAfterSeconds: number | null
  readonly effect: string | null
  readonly expiresAt: string | null

  constructor(
    status: number,
    message: string,
    code: string | null = null,
    retryAfterSeconds: number | null = null,
    effect: string | null = null,
    expiresAt: string | null = null,
  ) {
    super(message)
    this.name = 'PublicRequestError'
    this.status = status
    this.code = code
    this.retryAfterSeconds = retryAfterSeconds
    this.effect = effect
    this.expiresAt = expiresAt
  }
}

export class PlayerAccessRequiredError extends PublicRequestError {
  constructor(message = 'player account verification is required') {
    super(403, message, 'account_approval_required')
    this.name = 'PlayerAccessRequiredError'
  }
}

export const playerSortOptions: Array<{ value: PlayerSort; label: string }> = [
  { value: 'matches_played', label: 'Матчи' },
  { value: 'win_rate', label: 'Винрейт' },
  { value: 'avg_kills', label: 'Kills' },
  { value: 'avg_deaths', label: 'Deaths' },
  { value: 'avg_assists', label: 'Assists' },
  { value: 'total_duration_seconds', label: 'Время' },
  { value: 'last_match_at', label: 'Last match' },
]

export const publicApi = {
  status: () => requestJSON<PublicStatus>(`${apiBase}/status`),
  liveCount: (options: Pick<PublicRequestOptions, 'signal'> = {}) =>
    requestJSON<LiveMatchCountResponse>(`${apiBase}/live/count`, {
      cache: 'no-store',
      signal: options.signal,
    }),
  liveMatches: (options: Pick<PublicRequestOptions, 'signal'> = {}) =>
    requestJSON<LiveMatchListResponse>(`${apiBase}/live/matches`, {
      cache: 'no-store',
      signal: options.signal,
    }),
  liveMatch: (liveID: string, options: Pick<PublicRequestOptions, 'signal'> = {}) =>
    requestJSON<LiveMatch>(`${apiBase}/live/matches/${encodeURIComponent(liveID)}`, {
      cache: 'no-store',
      signal: options.signal,
    }),
  matches: (limit = 20, offset = 0, filters: MatchListFilters = {}) =>
    requestJSON<MatchListResponse>(`${apiBase}/matches?${params({
      limit,
      offset,
      game_mode: filters.gameMode,
      lobby_type: filters.lobbyType,
      lobby_types: filters.lobbyTypes,
      hero_id: filters.heroID,
      scoring_status: filters.scoringStatus,
    })}`),
  match: (matchID: string, options: PublicRequestOptions = {}) =>
    requestJSON<MatchDetail>(`${apiBase}/matches/${encodeURIComponent(matchID)}`, publicRequestInit(options)),
  players: (limit = 20, offset = 0, sort: PlayerSort = 'matches_played', dir: PlayerSortDir = 'desc') =>
    requestJSON<PlayerListResponse>(`${apiBase}/players?${params({ limit, offset, sort, dir })}`),
  player: (steamID: string, filters: MatchListFilters = {}, options: PublicRequestOptions = {}) =>
    requestJSON<PlayerProfile>(`${apiBase}/players/${encodeURIComponent(steamID)}?${params({
      lobby_types: filters.lobbyTypes,
      lobby_category: filters.lobbyCategory,
    })}`, publicRequestInit(options)),
  playerStatus: (
    steamID: string,
    options: Pick<PublicRequestOptions, 'signal'> = {},
  ) => requestJSON<PublicPlayerStatus>(
    `${apiBase}/players/${encodeURIComponent(steamID)}/status`,
    {
      cache: 'no-store',
      ...(options.signal ? { signal: options.signal } : {}),
    },
  ),
  playerMatches: (steamID: string, limit = 15, offset = 0, filters: MatchListFilters = {}) =>
    requestJSON<PlayerMatchListResponse>(`${apiBase}/players/${encodeURIComponent(steamID)}/matches?${params({
      limit,
      offset,
      lobby_types: filters.lobbyTypes,
      lobby_category: filters.lobbyCategory,
    })}`),
  playerPeers: (
    steamID: string,
    relation: PlayerPeerRelation,
    limit = 12,
    offset = 0,
    filters: MatchListFilters = {},
    options: PublicRequestOptions = {},
  ) => requestJSON<PlayerPeersResponse>(
    `${apiBase}/players/${encodeURIComponent(steamID)}/peers?${params({
      relation,
      limit,
      offset,
      lobby_types: filters.lobbyTypes,
      lobby_category: filters.lobbyCategory,
    })}`,
    publicRequestInit(options),
  ),
  heroes: () => requestJSON<PatchHeroesResponse>(`${apiBase}/patch/heroes`),
  heroProfile: (heroID: number, options: PublicRequestOptions = {}) =>
    requestJSON<HeroProfileResponse>(`${apiBase}/heroes/${heroID}`, publicRequestInit(options)),
  homeFeed: (options: PublicRequestOptions = {}) =>
    requestJSON<HomeFeedResponse>(`${apiBase}/home/feed`, publicRequestInit(options)),
  playerFeed: (steamID: string, options: PublicRequestOptions = {}) =>
    requestJSON<PlayerFeedResponse>(`${apiBase}/players/${encodeURIComponent(steamID)}/feed`, publicRequestInit(options)),
  heroMatches: (heroID: number, limit = 20, offset = 0, options: PublicRequestOptions = {}) =>
    requestJSON<HeroMatchListResponse>(
      `${apiBase}/heroes/${heroID}/matches?${params({ limit, offset })}`,
      publicRequestInit(options),
    ),
}

export const playerApi = {
  loginURL: (next = currentPath()) =>
    `/auth/steam/login?${params({ next })}`,
  me: async () => normalizePlayerMeResponse(
    await playerRequestJSON<PlayerMeResponse>(`${playerApiBase}/me`),
  ),
  accessApplication: () =>
    playerRequestJSON<PlayerAccessApplicationState>(`${playerApiBase}/access-application`),
  submitAccessApplication: (body: PlayerAccessApplicationSubmit) =>
    playerRequestJSON<PlayerAccessApplicationState>(
      `${playerApiBase}/access-application`,
      playerMutationInit('POST', body),
    ),
  dismissAccessWelcome: () =>
    playerRequestNoContent(
      `${playerApiBase}/access-welcome/dismiss`,
      playerMutationInit('POST'),
    ),
  matchChat: (matchID: string) =>
    playerRequestJSON<PlayerMatchChatResponse>(
      `${playerApiBase}/matches/${encodeURIComponent(matchID)}/chat`,
      { cache: 'no-store' },
    ),
  matchmakerState: (signal?: AbortSignal) => playerRequestJSON<PlayerMatchmakerState>(
    `${playerApiBase}/matchmaker/state`,
    signal ? { signal } : {},
  ),
  recordNotificationDelivery: (
    telemetry: PlayerNotificationDeliveryTelemetry,
    keepalive = false,
  ) => playerRequestNoContent(
    `${playerApiBase}/telemetry/notification-delivery`,
    {
      ...playerMutationInit('POST', telemetry),
      keepalive,
    },
  ),
  pushConfig: (signal?: AbortSignal) => playerRequestJSON<PlayerPushConfig>(
    `${playerApiBase}/push/config`,
    {
      cache: 'no-store',
      ...(signal ? { signal } : {}),
    },
  ),
  putPushSubscription: (
    deviceID: string,
    subscription: PlayerPushSubscription,
    sessionToken: string,
    signal?: AbortSignal,
  ) => playerRequestNoContent(
    `${playerApiBase}/push/subscriptions/${encodeURIComponent(deviceID)}`,
    {
      ...playerMutationInit('PUT', subscription, {
        'X-Phoenix-Push-Session-Token': sessionToken,
      }),
      ...(signal ? { signal } : {}),
    },
  ),
  deletePushSubscription: (
    deviceID: string,
    sessionToken: string,
    signal?: AbortSignal,
  ) => playerRequestNoContent(
    `${playerApiBase}/push/subscriptions/${encodeURIComponent(deviceID)}`,
    {
      ...playerMutationInit('DELETE', undefined, {
        'X-Phoenix-Push-Session-Token': sessionToken,
      }),
      ...(signal ? { signal } : {}),
    },
  ),
  partyState: async (): Promise<PlayerPartySnapshot> => {
    const result = await playerRequestJSONResponse<PlayerPartyState>(`${playerApiBase}/party`)
    return {
      state: result.data,
      etag: result.response.headers.get('ETag'),
    }
  },
  disbandParty: (etag: string) =>
    playerRequestJSON<Schemas['PartyOKResponse']>(
      `${playerApiBase}/party`,
      playerMutationInit('DELETE', undefined, { 'If-Match': etag }),
    ),
  leaveParty: (etag: string) =>
    playerPartyMutation<PlayerPartyResponse>(
      `${playerApiBase}/party/leave`,
      playerMutationInit('POST', undefined, { 'If-Match': etag }),
    ),
  invitePartyMember: (steamID: string, etag: string | null) =>
    playerPartyMutation<PlayerPartyInviteResponse>(
      `${playerApiBase}/party/invites`,
      playerMutationInit('POST', { steam_id: steamID }, {
        ...(etag ? { 'If-Match': etag } : {}),
      }),
    ),
  acceptPartyInvite: (inviteID: string, partyETag: string) =>
    playerPartyMutation<PlayerPartyResponse>(
      `${playerApiBase}/party/invites/${encodeURIComponent(inviteID)}/accept`,
      playerMutationInit('POST', undefined, { 'If-Match': partyETag }),
    ),
  declinePartyInvite: (inviteID: string) =>
    playerRequestJSON<Schemas['PartyOKResponse']>(
      `${playerApiBase}/party/invites/${encodeURIComponent(inviteID)}/decline`,
      playerMutationInit('POST'),
    ),
  declineAllPartyInvites: () =>
    playerRequestJSON<PlayerPartyInvitesDeclineAllResponse>(
      `${playerApiBase}/party/invites`,
      playerMutationInit('DELETE'),
    ),
  kickPartyMember: (steamID: string, etag: string) =>
    playerPartyMutation<PlayerPartyResponse>(
      `${playerApiBase}/party/members/${encodeURIComponent(steamID)}/kick`,
      playerMutationInit('POST', undefined, { 'If-Match': etag }),
    ),
  transferPartyLeadership: (steamID: string, etag: string) =>
    playerPartyMutation<PlayerPartyResponse>(
      `${playerApiBase}/party/leader`,
      playerMutationInit('PUT', { steam_id: steamID }, { 'If-Match': etag }),
    ),
  socialState: () =>
    playerRequestJSON<PlayerSocialState>(`${playerApiBase}/social/state`),
  sendFriendRequest: (steamID: string) =>
    playerRequestJSON<Schemas['FriendRequestResponse']>(
      `${playerApiBase}/friend-requests`,
      playerMutationInit('POST', { steam_id: steamID }),
    ),
  acceptFriendRequest: (requestID: string) =>
    playerRequestJSON<Schemas['FriendshipResponse']>(
      `${playerApiBase}/friend-requests/${encodeURIComponent(requestID)}/accept`,
      playerMutationInit('POST'),
    ),
  removeFriendRequest: (requestID: string) =>
    playerRequestJSON<Schemas['SocialOKResponse']>(
      `${playerApiBase}/friend-requests/${encodeURIComponent(requestID)}`,
      playerMutationInit('DELETE'),
    ),
  removeFriendship: (friendshipID: string) =>
    playerRequestJSON<Schemas['SocialOKResponse']>(
      `${playerApiBase}/friendships/${encodeURIComponent(friendshipID)}`,
      playerMutationInit('DELETE'),
    ),
  blockPlayer: (steamID: string) =>
    playerRequestJSON<Schemas['BlockResponse']>(
      `${playerApiBase}/blocks`,
      playerMutationInit('POST', { steam_id: steamID }),
    ),
  unblockPlayer: (blockID: string) =>
    playerRequestJSON<Schemas['SocialOKResponse']>(
      `${playerApiBase}/blocks/${encodeURIComponent(blockID)}`,
      playerMutationInit('DELETE'),
    ),
  ignorePlayerNotifications: (steamID: string) =>
    playerRequestJSON<Schemas['NotificationIgnoreResponse']>(
      `${playerApiBase}/notification-ignores`,
      playerMutationInit('POST', { steam_id: steamID }),
    ),
  unignorePlayerNotifications: (ignoreID: string) =>
    playerRequestJSON<Schemas['SocialOKResponse']>(
      `${playerApiBase}/notification-ignores/${encodeURIComponent(ignoreID)}`,
      playerMutationInit('DELETE'),
    ),
  heartbeatPresence: (clientID: string) =>
    playerRequestJSON<PlayerPresenceHeartbeat>(
      `${playerApiBase}/presence/heartbeat`,
      playerMutationInit('POST', { client_id: clientID }),
    ),
  releasePresence: (clientID: string, keepalive = false) =>
    playerRequestJSON<Schemas['SocialOKResponse']>(
      `${playerApiBase}/presence/release`,
      {
        ...playerMutationInit('POST', { client_id: clientID }),
        keepalive,
      },
    ),
  chatConversations: (limit = 100, cursor?: string) =>
    playerRequestJSON<PlayerChatConversationPage>(
      `${playerApiBase}/chat/conversations?${params({
        limit: chatConversationLimit(limit),
        cursor,
      })}`,
    ),
  chatConversation: (conversationID: string) =>
    playerRequestJSON<PlayerChatConversation>(
      `${playerApiBase}/chat/conversations/${encodeURIComponent(conversationID)}`,
    ),
  chatHistory: (
    conversationID: string,
    chatEpoch: string,
    options: { limit?: number; beforeID?: string; afterID?: string } = {},
  ) =>
    playerRequestJSON<PlayerChatHistory>(
      `${playerApiBase}/chat/conversations/${encodeURIComponent(conversationID)}/messages?${params({
        chat_epoch: chatEpoch,
        limit: chatHistoryLimit(options.limit),
        before_id: options.beforeID,
        after_id: options.afterID,
      })}`,
    ),
  markChatRead: (conversationID: string, chatEpoch: string, throughMessageID: string) =>
    playerRequestJSON<PlayerChatReadCursor>(
      `${playerApiBase}/chat/conversations/${encodeURIComponent(conversationID)}/read`,
      playerMutationInit('PUT', {
        chat_epoch: chatEpoch,
        through_message_id: throughMessageID,
      }),
    ),
  hideDMConversationFromRecents: (conversationID: string) =>
    playerRequestJSON<Schemas['PlayerChatOK']>(
      `${playerApiBase}/chat/conversations/${encodeURIComponent(conversationID)}/recent`,
      playerMutationInit('DELETE'),
    ),
  sendPartyMessage: (text: string, etag: string | null, idempotencyKey: string) =>
    playerRequestJSON<PlayerChatSendResponse>(
      `${playerApiBase}/chat/party/messages`,
      playerMutationInit('POST', { text }, {
        'Idempotency-Key': idempotencyKey,
        ...(etag ? { 'If-Match': etag } : {}),
      }),
    ),
  sendGlobalMessage: (text: string, idempotencyKey: string) =>
    playerRequestJSON<PlayerChatSendResponse>(
      `${playerApiBase}/chat/global/messages`,
      playerMutationInit('POST', { text }, {
        'Idempotency-Key': idempotencyKey,
      }),
    ),
  sendDMMessage: (steamID: string, text: string, idempotencyKey: string) =>
    playerRequestJSON<PlayerChatSendResponse>(
      `${playerApiBase}/chat/dms/${encodeURIComponent(steamID)}/messages`,
      playerMutationInit('POST', { text }, {
        'Idempotency-Key': idempotencyKey,
      }),
    ),
  reportChatMessage: (
    conversationID: string,
    messageID: string,
    chatEpoch: string,
    reason: Schemas['PlayerChatReportRequest']['reason'],
  ) =>
    playerRequestJSON<PlayerChatReportResponse>(
      `${playerApiBase}/chat/conversations/${encodeURIComponent(conversationID)}/messages/${encodeURIComponent(messageID)}/reports`,
      playerMutationInit('POST', { chat_epoch: chatEpoch, reason }),
    ),
  joinQueue: (queueID: string) =>
    playerRequestJSON<ResultResponse>(`${playerApiBase}/queues/${encodeURIComponent(queueID)}/join`, {
      method: 'POST',
      credentials: 'same-origin',
    }),
  leaveQueue: (queueID: string) =>
    playerRequestJSON<ResultResponse>(`${playerApiBase}/queues/${encodeURIComponent(queueID)}/leave`, {
      method: 'POST',
      credentials: 'same-origin',
    }),
  leaveMatch: () =>
    playerRequestJSON<ResultResponse>(`${playerApiBase}/match/leave`, {
      method: 'POST',
      credentials: 'same-origin',
    }),
  acceptReadyCheck: () =>
    playerRequestJSON<ResultResponse>(`${playerApiBase}/ready-check/accept`, {
      method: 'POST',
      credentials: 'same-origin',
    }),
  declineReadyCheck: () =>
    playerRequestJSON<ResultResponse>(`${playerApiBase}/ready-check/decline`, {
      method: 'POST',
      credentials: 'same-origin',
    }),
  logout: () =>
    requestJSON<LogoutResponse>('/auth/logout', {
      method: 'POST',
      credentials: 'same-origin',
    }),
}

async function playerRequestJSON<T>(url: string, init: RequestInit = {}): Promise<T> {
  try {
    return await requestJSON<T>(url, init)
  } catch (error) {
    if (error instanceof ResponseUnauthorizedError) {
      throw new PlayerSessionUnauthorizedError()
    }
    throw error
  }
}

async function playerRequestNoContent(
  url: string,
  init: RequestInit = {},
): Promise<void> {
  try {
    await requestNoContent(url, init)
  } catch (error) {
    if (error instanceof ResponseUnauthorizedError) {
      throw new PlayerSessionUnauthorizedError()
    }
    throw error
  }
}

async function playerRequestJSONResponse<T>(
  url: string,
  init: RequestInit = {},
): Promise<{ data: T; response: Response }> {
  try {
    return await requestJSONResponse<T>(url, init)
  } catch (error) {
    if (error instanceof ResponseUnauthorizedError) {
      throw new PlayerSessionUnauthorizedError()
    }
    throw error
  }
}

async function playerPartyMutation<T>(
  url: string,
  init: RequestInit,
): Promise<PlayerPartyMutationResult<T>> {
  const result = await playerRequestJSONResponse<T>(url, init)
  return {
    data: result.data,
    etag: result.response.headers.get('ETag'),
  }
}

async function requestJSON<T>(url: string, init: RequestInit = {}): Promise<T> {
  return (await requestJSONResponse<T>(url, init)).data
}

async function requestJSONResponse<T>(
  url: string,
  init: RequestInit = {},
): Promise<{ data: T; response: Response }> {
  const response = await requestResponse(url, init)
  return {
    data: await response.json() as T,
    response,
  }
}

async function requestNoContent(
  url: string,
  init: RequestInit = {},
): Promise<void> {
  await requestResponse(url, init)
}

async function requestResponse(
  url: string,
  init: RequestInit = {},
): Promise<Response> {
  const headers = new Headers(init.headers)
  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json')
  }
  const response = await fetch(url, {
    ...init,
    credentials: init.credentials ?? 'same-origin',
    headers,
  })
  if (response.status === 401) {
    throw new ResponseUnauthorizedError()
  }
  if (!response.ok) {
    const error = await responseError(response)
    if (response.status === 403 && isPlayerAccessRequiredCode(error.code)) {
      throw new PlayerAccessRequiredError(error.message)
    }
    throw new PublicRequestError(
      response.status,
      error.message,
      error.code,
      retryAfterSeconds(response.headers.get('Retry-After')),
      error.effect,
      error.expiresAt,
    )
  }
  return response
}

export function normalizePlayerMeResponse(response: PlayerMeResponse): PlayerMeResponse {
  const raw = response as PlayerMeResponse & { access?: Partial<PlayerAccessStatus> }
  const access = raw.access
  const allowed = access?.allowed !== false
  const required = access?.required === true
  const status = access?.status === 'approved' ||
    access?.status === 'verification_required' ||
    access?.status === 'not_required'
    ? access.status
    : required && !allowed
      ? 'verification_required'
      : required
        ? 'approved'
        : 'not_required'
  return {
    ...response,
    capabilities: {
      ...response.capabilities,
      chat: normalizePlayerChatCapabilities(response.capabilities.chat),
    },
    restrictions: normalizePlayerRestrictions(response.restrictions),
    access: {
      required,
      allowed,
      status,
      contact_url: typeof access?.contact_url === 'string'
        ? access.contact_url
        : null,
      welcome_pending: status === 'approved' && access?.welcome_pending === true,
    },
  }
}

function normalizePlayerChatCapabilities(
  capabilities: PlayerChatCapabilities,
): PlayerChatCapabilities {
  const rawSend = capabilities.send
  return {
    ...capabilities,
    send: {
      global: typeof rawSend?.global === 'boolean'
        ? rawSend.global
        : capabilities.global,
      party: typeof rawSend?.party === 'boolean'
        ? rawSend.party
        : capabilities.party,
      dm: typeof rawSend?.dm === 'boolean'
        ? rawSend.dm
        : capabilities.dm,
    },
  }
}

function normalizePlayerRestrictions(
  restrictions: PlayerMeResponse['restrictions'],
): PlayerMeResponse['restrictions'] {
  const rawCommunication = restrictions?.communication
  const rawEffects = rawCommunication?.effects
  if (!rawCommunication || !Array.isArray(rawEffects)) return restrictions
  const communication: PlayerCommunicationRestrictions = {
    effects: rawEffects.filter(effect => (
      effect && typeof effect.effect === 'string'
    )),
  }
  if (typeof rawCommunication.next_policy_change_at === 'string') {
    communication.next_policy_change_at = rawCommunication.next_policy_change_at
  }
  return {
    ...restrictions,
    communication,
  }
}

function isPlayerAccessRequiredCode(code: string | null): boolean {
  return code === 'account_approval_required' ||
    code === 'account_verification_required' ||
    code === 'player_access_required' ||
    code === 'verification_required'
}

function retryAfterSeconds(raw: string | null): number | null {
  if (!raw || !/^[0-9]+$/.test(raw.trim())) return null
  const seconds = Number(raw)
  return Number.isSafeInteger(seconds) && seconds > 0 ? seconds : null
}

class ResponseUnauthorizedError extends Error {
  constructor() {
    super('unauthorized')
    this.name = 'ResponseUnauthorizedError'
  }
}

async function responseError(response: Response): Promise<{
  message: string
  code: string | null
  effect: string | null
  expiresAt: string | null
}> {
  try {
    const body = await response.json() as {
      error?: string
      code?: string
      effect?: string
      expires_at?: string
    }
    if (body.error) {
      return {
        message: body.error,
        code: body.code || null,
        effect: body.effect || null,
        expiresAt: body.expires_at || null,
      }
    }
  } catch {
    // Fall through to status text.
  }
  return {
    message: `${response.status} ${response.statusText}`.trim(),
    code: null,
    effect: null,
    expiresAt: null,
  }
}

function playerMutationInit(
  method: 'POST' | 'PUT' | 'DELETE',
  body?: unknown,
  extraHeaders: Record<string, string> = {},
): RequestInit {
  const headers = new Headers(extraHeaders)
  const init: RequestInit = {
    method,
    credentials: 'same-origin',
    headers,
  }
  if (body !== undefined) {
    headers.set('Content-Type', 'application/json')
    init.body = JSON.stringify(body)
  }
  return init
}

function chatHistoryLimit(value: number | undefined): number {
  if (value === undefined || !Number.isFinite(value)) return 100
  return Math.min(100, Math.max(1, Math.trunc(value)))
}

function chatConversationLimit(value: number | undefined): number {
  if (value === undefined || !Number.isFinite(value)) return 100
  return Math.min(100, Math.max(1, Math.trunc(value)))
}

function params(input: Record<string, string | number | readonly number[] | null | undefined>): string {
  const out = new URLSearchParams()
  for (const [key, value] of Object.entries(input)) {
    if (value === null || value === undefined || value === '') continue
    if (Array.isArray(value)) {
      if (value.length === 0) continue
      out.set(key, value.join(','))
      continue
    }
    out.set(key, String(value))
  }
  return out.toString()
}

function currentPath(): string {
  return `${window.location.pathname}${window.location.search}` || '/'
}

function publicRequestInit(options: PublicRequestOptions): RequestInit {
  const init: RequestInit = {}
  if (options.signal) init.signal = options.signal
  if (options.fresh) {
    init.cache = 'no-cache'
    init.headers = {
      'Cache-Control': 'no-cache',
    }
  }
  return init
}
